<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter, onBeforeRouteUpdate, onBeforeRouteLeave } from 'vue-router'
import { useCustomerStore } from '@/pinia/customer'
import { storeToRefs } from 'pinia'

const route = useRoute()
const router = useRouter()

const customerStore = useCustomerStore()
const { customer } = storeToRefs(customerStore)

const products = ref([])
const financials = ref({})
const originalFinancials = ref({})
const financialCategories = ref([])
const discounts = ref([])

const isLoadingProducts = ref(true)
const isLoadingFinancials = ref(true)

const isChanged = computed(() => {
  return JSON.stringify(financials.value) !== JSON.stringify(originalFinancials.value)
})

onMounted(async () => {
  await getProducts()
  if (route.query.productId) {
    await getFinancials()
  } else {
    router.replace({ query: { ...route.query, productId: products.value?.[0]?.engineId } })
  }
  financialCategories.value = await window.touch.GetFinancialCategories()
  discounts.value = await window.touch.staffListDiscountCategories()
})

const getProducts = async () => {
  isLoadingProducts.value = true
  const GLASS_TYPE_ID = 1
  const { products: fetchedProducts } = await window.touch.GetSimpleProducts({
    datasetId: customer.value.datasetIds[0],
    typeId: GLASS_TYPE_ID
  })
  products.value = fetchedProducts
  isLoadingProducts.value = false
}

const getFinancials = async () => {
  isLoadingFinancials.value = true
  financials.value = await window.touch.GetGlassFinancials({
    datasetId: customer.value.datasetIds[0],
    productId: route.query.productId
  })
  originalFinancials.value = JSON.parse(JSON.stringify(financials.value))
  isLoadingFinancials.value = false
}

const save = async () => {
  isLoadingFinancials.value = true
  try {
    const changed = getChanged()
    await window.touch.UpdateGlassFinancials({
      ...changed,
      DatasetId: customer.value.datasetIds[0]
    })
  } catch (error) {
    if (error.status === 400) {
      alertBox().fire(error.popUp)
    } else {
      throw error
    }
  }
  await getFinancials()
  isLoadingFinancials.value = false
}


const getChanged = () => {
  let changedItems = []
  if (isChanged.value) {
    for (let i = 0; i < financials.value.items.length; i++) {
      const item = financials.value.items[i]
      const original = originalFinancials.value.items[i]
      if (JSON.stringify(item) !== JSON.stringify(original)) {
        changedItems.push({
          ...item,
          financialId: item.financialId === '00000000-0000-0000-0000-000000000000' ? undefined : item.financialId
        })
      }
    }
  }

  return {
    ProductId: financials.value.productId,
    FinancialCategoryId: financials.value.financialCategoryId,
    DiscountCategoryId: financials.value.discountCategoryId,
    MinimumAreaForCosting: financials.value.minimumAreaForCosting,
    MinimumAreaForPricing: financials.value.minimumAreaForPricing,
    NonRectangularPremium: financials.value.nonRectangularPremium,
    GlassFinancialItems: changedItems
  }
}

const promptToSave = (callback) => {
  if (isChanged.value) {
    alertBox()
      .fire({
        title: 'There are unsaved changes',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Go Back',
        confirmButtonText: 'Continue'
      })
      .then((result) => {
        if (result.value) {
          callback()
        }
      })
  } else {
    callback()
  }
}

watch(
  () => route.query.productId,
  async (productId) => {
    if (productId) {
      await getFinancials()
    }
  }
)

onBeforeRouteUpdate((to, from, next) => {
  promptToSave(() => next())
})

onBeforeRouteLeave((to, from, next) => {
  if (to.name === 'Error') {
    next()
  } else {
    promptToSave(() => next())
  }
})

</script>


<template>
  <touch-layout>
    <template #header_page_breadcrumb>
      <slot name="header_page_breadcrumb" />
    </template>
    <template #header_page_title>
      <slot name="header_page_title" />
    </template>
    <slot name="menu" />
    <div class="flex flex-col grow">
      <div class="flex flex-col md:flex-row gap-5 p-5 grow bg-gray-200 w-full overflow-y-auto md:overflow-y-auto" style="flex: 1 1 0">
        <div class="flex flex-col gap-2 bg-white p-5 border border-gray shrink-0" style="min-width: 250px">
          <loading v-if="isLoadingProducts" :loading="true" class="h-full" style="min-height: 200px; flex: 1 1 0" />
          <div
            v-else-if="products.length > 0"
            class="overflow-y-auto" 
            style="min-height: 200px; min-width: 200px; flex: 1 1 0"
          >
            <div
              v-for="product in products"
              :key="product.id"
              class="mb-2 p-2 rounded bg-gray-200 cursor-pointer"
              :class="{ 'bg-green-600 text-white': product.engineId == $route.query.productId }"
              @click="$router.push({ query: { ...$route.query, productId: product.engineId } })"
            >
              {{ product.description }}
            </div>
          </div>
          <div v-else class="overflow-hidden flex flex-col grow justify-around text-center px-5"> No Products Found</div>
        </div>
        <div class="flex flex-col w-full gap-5">
          <div class="flex gap-10 bg-white p-5 w-full border border-gray">
            <div
              class="cursor-pointer border-b-4 hover:border-black" 
              :class="[(typeof $route.query.type === 'undefined') ? 'border-black' : 'border-white']"
              @click="$router.push({ query: { ...$route.query, type: undefined } })"
            >
              General
            </div>
            <div
              class="cursor-pointer border-b-4 hover:border-black" 
              :class="[$route.query.type == 0 ? 'border-black' : 'border-white']"
              @click="$router.push({ query: { ...$route.query, type: 0 } })"
            >
              Types
            </div>
            <div
              class="cursor-pointer border-b-4 hover:border-black" 
              :class="[$route.query.type == 1 ? 'border-black' : 'border-white']"
              @click="$router.push({ query: { ...$route.query, type: 1 } })"
            >
              Patterns
            </div>
            <div
              class="cursor-pointer border-b-4 hover:border-black" 
              :class="[$route.query.type == 2 ? 'border-black' : 'border-white']"
              @click="$router.push({ query: { ...$route.query, type: 2 } })"
            >
              Space Bars
            </div>
            <div
              class="cursor-pointer border-b-4 hover:border-black" 
              :class="[$route.query.type == 3 ? 'border-black' : 'border-white']"
              @click="$router.push({ query: { ...$route.query, type: 3 } })"
            >
              Decorations
            </div>
            <div
              class="cursor-pointer border-b-4 hover:border-black" 
              :class="[$route.query.type == 4 ? 'border-black' : 'border-white']"
              @click="$router.push({ query: { ...$route.query, type: 4 } })"
            >
              Gas Fill
            </div>
            <div
              class="cursor-pointer border-b-4 hover:border-black" 
              :class="[$route.query.type == 5 ? 'border-black' : 'border-white']"
              @click="$router.push({ query: { ...$route.query, type: 5 } })"
            >
              Fan Holes
            </div>
          </div>
          <div class="flex flex-col gap-2 bg-white p-5 w-full border border-gray" style="flex: 1 1 0">
            <loading v-if="isLoadingFinancials" :loading="true" class="h-full" style="min-height: 200px; flex: 1 1 0" />
            <div v-else-if="(typeof $route.query.type === 'undefined')" class="grid grid-cols-2 gap-5 p-5 max-w-6xl">
              <div class="flex flex-col gap-1">
                <div>
                  Financial Category
                </div>
                <div>
                  <select v-model="financials.financialCategoryId" class="border w-full rounded p-2 bg-white">
                    <option :value="0">None</option>
                    <option v-for="financialCategory in financialCategories" :key="financialCategory.id" :value="financialCategory.id">
                      {{ financialCategory.description }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div>
                  Discount Category
                </div>
                <div>
                  <select v-model="financials.discountCategoryId" class="border w-full rounded p-2 bg-white">
                    <option :value="0">None</option>
                    <option v-for="discount in discounts" :key="discount.id" :value="discount.id">
                      {{ discount.description }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div>
                  Minimum Area for Costing
                </div>
                <div class="flex items-center bg-white rounded border overflow-hidden w-full">
                  <input
                    v-model.number="financials.minimumAreaForCosting"
                    type="number"
                    class="grow p-2 w-24"
                    min="0"
                  >
                  <div class="border-l px-2">
                    <span>m&sup2;</span>
                  </div>
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div>
                  Minimum Area for Pricing
                </div>
                <div class="flex items-center bg-white rounded border overflow-hidden w-full">
                  <input
                    v-model.number="financials.minimumAreaForPricing"
                    type="number"
                    class="grow p-2 w-24"
                    min="0"
                  >
                  <div class="border-l px-2">
                    <span>m&sup2;</span>
                  </div>
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div>
                  Non Rectangular Premium
                </div>
                <div class="flex items-center bg-white rounded border overflow-hidden w-full">
                  <input
                    v-model.number="financials.nonRectangularPremium"
                    type="number"
                    class="grow p-2 w-24"
                    min="0"
                  >
                  <div class="border-l px-2">
                    <span>%</span>
                  </div>
                </div>
              </div>
            </div>
            <div 
              v-else-if="financials.items.filter(x => x.type == $route.query.type).length > 0"
              class="md:overflow-y-auto"
              style="flex: 1 1 0"
            >
              <div class="grow bg-white">
                <table class="table-pattern table-auto border-collapse" style="min-width: 500px;">
                  <thead class="sticky top-0 bg-white z-30 w-full">
                    <tr>
                      <th class="pl-4 pb-5 text-left">Description</th>
                      <th class="pl-4 pb-5 text-left">Cost</th>
                      <th class="pl-4 pb-5 text-left">Price</th>
                    </tr>
                  </thead>
                  <tbody class="w-full">
                    <tr v-for="financial in financials.items.filter(x => x.type == $route.query.type)" :key="financial.id">
                      <td class="px-4 py-2">
                        {{ financial.description }}
                      </td>
                      <td class="px-4 py-2">
                        <div class="flex items-center bg-white rounded border overflow-hidden w-40">
                          <div class="border-r px-2">
                            <span>£</span>
                          </div>
                          <input
                            v-model.number="financial.cost"
                            type="number"
                            class="grow p-2 w-24"
                            min="0"
                          >
                        </div>
                      </td>
                      <td class="px-4 py-2">
                        <div class="flex items-center bg-white rounded border overflow-hidden w-40">
                          <div class="border-r px-2">
                            <span>£</span>
                          </div>
                          <input
                            v-model.number="financial.price"
                            type="number"
                            class="grow p-2 w-24"
                            min="0"
                          >
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="overflow-hidden flex flex-col grow justify-around text-center px-5"> No Financials Found</div>
          </div>
        </div>
      </div>
      <div class="sticky bottom-0 bg-white text-right p-3 border-t">
        <button
          class="btn btn-action btn-lg"
          :class="{ 'opacity-50 pointer-events-none bg-gray-500 border-gray-500': !isChanged }"
          @click="save()"
        >
          Save Changes
        </button>
      </div>
    </div>
  </touch-layout>
</template>